import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import { PlatformService } from './platform.service';
import { Meta, Title } from '@angular/platform-browser';
import { config } from '../../../../shared/config';
import { environment } from '../../environments/environment';
import { generateOGImage } from '../helpers/og';

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  constructor(
    private platform: PlatformService,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Inject a JSON-LD structured-data block into <head>.
   *
   * Runs on the server too — Angular renders into a real DOM during SSR, so the
   * script lands in the prerendered HTML where crawlers can actually see it.
   * Keyed by `id` and replaced rather than appended, so navigating between
   * routes can't stack duplicate blocks for the same schema.
   */
  setJsonLd(id: string, data: unknown): void {
    const existing = this.document.getElementById(id);
    if (existing) existing.remove();

    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  setMeta(
    rawTitle: string,
    description: string,
    sportType?: string,
    about?: 'Club' | 'Event' | 'Session' | 'Tournament' | 'Venue' | 'Marketplace' | 'Coach' | 'Match' | 'Sponsor'
  ): void {
    const titlePrefix = sportType && about ? `${sportType} ${about} - ` : '';
    const title = titlePrefix + rawTitle;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });

    // Facebook
    this.meta.updateTag({
      property: 'og:title',
      content: title,
    });
    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });
    this.meta.updateTag({
      property: 'og:image',
      content:
        about && sportType
          ? generateOGImage(rawTitle, description, sportType, about)
          : config.defaultFeaturedImage,
    });
    this.meta.updateTag({
      property: 'og:url',
      content: environment.websiteUrl,
    });
    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    // Twitter
    this.meta.updateTag({
      name: 'twitter:title',
      content: title,
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
    this.meta.updateTag({
      name: 'twitter:image',
      content:
        about && sportType
          ? generateOGImage(rawTitle, description, sportType, about)
          : config.defaultFeaturedImage,
    });
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
  }
}
